import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState(
        { cartDrawerOpen: true },
        ''
      );
    }

    this.#historyAbortController = new AbortController();

    window.addEventListener(
      'popstate',
      this.#handlePopState,
      {
        signal: this.#historyAbortController.signal
      }
    );
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.refs.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  /**
   * Handles cart add events.
   *
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(
      event.detail.resource?.item_count
    );
  };

  /**
   * Announces cart count to screen readers.
   *
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion =
      /** @type {HTMLElement | undefined} */
      (this.refs.liveRegion);

    if (
      !this.refs.dialog?.open ||
      !liveRegion ||
      cartCount === undefined
    ) {
      return;
    }

    liveRegion.textContent =
      `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /*
      Close cart drawer when installments CTA is clicked
      to avoid overlapping dialogs.
    */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const installmentsContent =
          document
            .querySelector('shopify-payment-terms')
            ?.shadowRoot;

        const cta =
          installmentsContent
            ?.querySelector('#shopify-installments-cta');

        cta?.addEventListener(
          'click',
          this.closeDialog,
          { once: true }
        );
      });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } =
      /** @type {Refs} */
      (this.refs);

    if (!dialog) return;

    const content =
      dialog.querySelector('.cart-drawer__content');

    const summary =
      dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}

/* =========================================
   MINI-KORPA – CENA NESTANDARDNE DIMENZIJE
   ========================================= */

(function () {
  'use strict';

  const CUSTOM_DIMENSION =
    'Nestandardna dimenzija';

  const CUSTOM_PRICE_TEXT =
    'Cena nakon porudžbine';

  const CUSTOM_INFO_ID =
    'raccoon-custom-dimension-cart-info';

  const CUSTOM_INFO_TEXT =
    'Prikazani ukupan iznos ne uključuje cenu proizvoda sa nestandardnom dimenzijom. Tačnu cenu dostavićemo nakon porudžbine.';

  let updateScheduled = false;

  function cleanText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isCustomDimensionRow(row) {
    return cleanText(row.textContent)
      .includes(CUSTOM_DIMENSION);
  }

  function updateCustomDimensionInfo(drawer, totalElement, hasCustomProducts) {
    let infoBox = drawer.querySelector('#' + CUSTOM_INFO_ID);

    if (!hasCustomProducts) {
      if (infoBox) infoBox.remove();
      return;
    }

    if (!infoBox) {
      infoBox = document.createElement('div');
      infoBox.id = CUSTOM_INFO_ID;
      infoBox.setAttribute('role', 'note');

      infoBox.style.cssText = [
        'display:flex',
        'align-items:flex-start',
        'gap:10px',
        'width:100%',
        'box-sizing:border-box',
        'margin-top:12px',
        'padding:13px 14px',
        'border:1px solid #1fa1ae',
        'border-radius:12px',
        'background:#E2F5F8',
        'color:#1fa1ae',
        'font-size:12px',
        'font-weight:600',
        'line-height:1.45'
      ].join(';');

      infoBox.innerHTML = `
  <span
    aria-hidden="true"
    style="
      display:inline-flex;
      align-items:center;
      justify-content:center;
      flex:0 0 22px;
      width:22px;
      height:22px;
      margin-top:1px;
      border-radius:50%;
      background:#1FA1AE;
      color:#ffffff;
      font-size:14px;
      font-weight:700;
      line-height:1;
    "
  >i</span>

  <span style="display:block;flex:1 1 auto;">
    <span style="display:block;font-weight:400;">
      Prikazani ukupan iznos ne uključuje cenu proizvoda sa nestandardnom dimenzijom.
    </span>

    <span style="display:block;font-weight:600;margin-top:3px;">
      Tačnu cenu dostavićemo nakon porudžbine.
    </span>
  </span>
`;
    }

    const totalRow =
      totalElement.closest(
        '.cart-totals__item.cart-totals__total'
      ) ||
      totalElement.closest(
        '.cart-totals__total'
      ) ||
      totalElement.parentElement;

    if (!totalRow || !totalRow.parentElement) {
      return;
    }

    if (totalRow.nextElementSibling !== infoBox) {
      totalRow.insertAdjacentElement(
        'afterend',
        infoBox
      );
    }
  }

  function updateCustomDimensionPrices() {
    const drawer =
      document.querySelector(
        'cart-drawer-component'
      );

    if (!drawer) return;

    const allRows = Array.from(
      drawer.querySelectorAll(
        '.cart-items__table-row'
      )
    );

    /*
      Menjanje pojedinačnih cena kod proizvoda
      sa nestandardnom dimenzijom.
    */
    allRows.forEach(function (row) {
      if (!isCustomDimensionRow(row)) return;

      row
        .querySelectorAll(
          '.tpo_cart_item_price, ' +
          '.cart-items__price text-component, ' +
          '.cart-items__unit-price-wrapper ' +
          'span:not(.visually-hidden)'
        )
        .forEach(function (priceElement) {
          priceElement.textContent =
            CUSTOM_PRICE_TEXT;

          if (
            priceElement.hasAttribute('value')
          ) {
            priceElement.setAttribute(
              'value',
              CUSTOM_PRICE_TEXT
            );
          }
        });
    });

    /*
      Zbirna cena na dnu mini-korpe.
      Uzimamo samo stvarne redove proizvoda, jer Horizon
      može imati i dodatne skrivene ili pomoćne redove.
    */
    const productRows = allRows.filter(function (row) {
      return Boolean(
        row.querySelector(
          '.cart-items__title, .cart-items__product-info'
        )
      );
    });

    const totalElement = drawer.querySelector(
      'text-component[ref="cartTotal"], ' +
      '[data-testid="cart-total-value"], ' +
      '.cart-totals__total-value'
    );

    if (!totalElement || productRows.length === 0) {
      return;
    }

    const allProductsAreCustom =
      productRows.every(function (row) {
        return isCustomDimensionRow(row);
      });

    const hasCustomProducts =
      productRows.some(function (row) {
        return isCustomDimensionRow(row);
      });

    updateCustomDimensionInfo(
      drawer,
      totalElement,
      hasCustomProducts
    );

    /*
      Samo kada su svi stvarni proizvodi u mini-korpi
      sa opcijom „Nestandardna dimenzija“, zbirnu cenu
      prikazujemo kao tekst.
    */
    if (allProductsAreCustom) {
      totalElement.setAttribute(
        'value',
        CUSTOM_PRICE_TEXT
      );

      totalElement.value =
        CUSTOM_PRICE_TEXT;

      totalElement.textContent =
        CUSTOM_PRICE_TEXT;

      totalElement.style.fontWeight = '600';

    }
  }

  function scheduleUpdate() {
    if (updateScheduled) return;

    updateScheduled = true;

    requestAnimationFrame(function () {
      updateScheduled = false;
      updateCustomDimensionPrices();
    });
  }

  /*
    Kada se mini-korpa otvori.
  */
  document.addEventListener(
    DialogOpenEvent.eventName,
    function (event) {
      const drawer =
        event.target instanceof Element
          ? event.target.closest(
              'cart-drawer-component'
            )
          : null;

      if (!drawer) return;

      setTimeout(
        updateCustomDimensionPrices,
        0
      );

      setTimeout(
        updateCustomDimensionPrices,
        100
      );

      setTimeout(
        updateCustomDimensionPrices,
        300
      );

      setTimeout(
        updateCustomDimensionPrices,
        700
      );
    },
    true
  );

  /*
    Kada se novi proizvod doda u korpu.
  */
  document.addEventListener(
    CartAddEvent.eventName,
    function () {
      setTimeout(
        updateCustomDimensionPrices,
        0
      );

      setTimeout(
        updateCustomDimensionPrices,
        150
      );

      setTimeout(
        updateCustomDimensionPrices,
        400
      );

      setTimeout(
        updateCustomDimensionPrices,
        800
      );
    }
  );

  /*
    Kada Horizon ponovo iscrta sadržaj korpe.
  */
  const observer =
    new MutationObserver(function () {
      scheduleUpdate();
    });

  function initialize() {
    const drawer =
      document.querySelector(
        'cart-drawer-component'
      );

    if (!drawer) return;

    updateCustomDimensionPrices();

    observer.observe(drawer, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['value']
    });

    setTimeout(
      updateCustomDimensionPrices,
      300
    );

    setTimeout(
      updateCustomDimensionPrices,
      800
    );

    setTimeout(
      updateCustomDimensionPrices,
      1500
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }
})();